<script setup>
import { ref, reactive, computed, toRef, watch } from 'vue';

// Components
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import InputNumber from 'primevue/inputnumber';

// Config components
import TextControl from '@/components/controls/textControl.vue';

// Element and template from parent props definition
const props = defineProps({
    element: {
        type: Object,
        required: true
    },
    template: {
        type: Object,
        required: true
    },
    configComponents: {
        type: Object,
        default: () => ({})
    },
    countdown: {
        type: Number,
        default: null
    },
    showQuickEdit: {
        type: Boolean,
        default: false
    }
});

// Emits
const emit = defineEmits(['requestConfig', 'requestCopy', 'requestDelete', 'requestPlay', 'requestStop', 'update:element', 'updateData']);

// Get reactive element and template
const element = toRef(props, 'element');
const template = toRef(props, 'template');

watch(element, (newValue) => {
    emit('update:element', newValue);
});

// Función para actualizar un dato específico del elemento
const updateElementData = (propname, value) => {
    element.value.data[propname] = value;
    emit('updateData', element.value.id, propname, value);
};

// Computed controls
const cardControls = computed(() => {
    const result = [];
    template.value.controls.sections.forEach(element => {
        element.items.forEach(item => {
            if (item.card === true) {
                result.push(item);
            }
        });
    });
    return result;
});

// Auto disable - usar directamente el valor del elemento
const autoDisable = computed({
    get: () => element.value.autooff > 0,
    set: (val) => {
        element.value.autooff = val ? (element.value.autooff || 5) : 0;
    }
});

const autoDisableSeconds = computed({
    get: () => element.value.autooff,
    set: (val) => {
        element.value.autooff = val || 0;
    }
});

// ANCHOR: Requests
function requestConfig() {
    emit('requestConfig', element.value);
}

function requestCopy() {
    emit('requestCopy', element.value);
}

function requestDelete() {
    emit('requestDelete', element.value);
}

function requestPlay() {
    emit('requestPlay', element.value);
}

function requestStop() {
    emit('requestStop', element.value);
}

</script>

<template>
    <div class="shadow-2 flex flex-column border-teal-600 bg-gray-100 px-2 pb-2 border-1 border-round">
        <!-- Top description -->
        <div class="w-full flex flex-row justify-content-between align-items-center drag-from-here">
            <div class="overflow-hidden block cursor-move pt-2 pb-2"
                style="text-overflow: ellipsis; white-space: nowrap;">
                ☰ {{ element.name }}
            </div>
            <div class="pl-2 h-full textheight justify-content-center align-items-end align-content-end">
                <div class="flex flex-row align-content-end justify-content-end">
                    <Button class="mr-1 py-1 px-1 shadow-3 border-1 border-black-alpha-30" size="small" icon="pi pi-cog"
                        severity="secondary" @click="requestConfig" />
                    <Button class="mr-1 py-1 px-1 shadow-3 border-1 border-black-alpha-30" size="small"
                        icon="pi pi-clone" severity="secondary" @click="requestCopy" />
                    <Button class="mr-0 py-1 px-1 shadow-3 border-1 border-black-alpha-30" size="small"
                        icon="pi pi-trash" severity="secondary" @click="requestDelete" />
                </div>
            </div>
        </div>

        <div v-if="showQuickEdit" class="flex flex-column">
            <div v-for="control in cardControls" :key="control.id">
                <component :is="configComponents[control.type]" 
                    :content="element.data[control.id]"
                    @update:content="updateElementData(control.id, $event)"
                    :validation="control.validation" 
                    :element="control" />
            </div>
        </div>

        <!-- Play buttons -->
        <div class="flex flex-row mt-1 justify-content-between align-items-center">
            <div class="flex flex-row align-items-center">
                <Button class="mr-2 py-1 px-2 bg-teal-600 border-teal-600" label="Play" icon="pi pi-play"
                    size="small" @click="requestPlay" />
                <Button class="mr-1 py-1 px-2 bg-teal-500 border-teal-500" 
                    :label="countdown ? `Stop (${countdown})` : 'Stop'" 
                    icon="pi pi-stop"
                    size="small" @click="requestStop" />
            </div>
            <div class="flex flex-row align-items-center">
                <div class="flex align-items-center gap-2 pl-1 pr-1">
                    <Checkbox v-model="autoDisable" binary inputId="auto" size="small" />
                    <label for="auto" class="text-sm">Auto</label>
                </div>
                <InputNumber inputClass="mininumber" v-model="autoDisableSeconds" inputId="autoseconds" suffix="s"
                    :min="0" size="small" />
            </div>
        </div>
    </div>
</template>

<style>
.mininumber {
    width: 4em !important;
    padding-left: 6px;
    padding-right: 6px;
}

.textheight {
    height: 1.6em;
}

.buttonspad {
    width: calc(100%);
    padding-top: 0.1em;
}
</style>